import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ToggleSwitch from '@/components/ui/ToggleSwitch/ToggleSwitch';
import { useGetAuthApi, usePutAuthApi } from '@/hooks/UseApiCalls';
import { ReturnedPaymentImpactDtoIn } from '@/models/in/ReturnedPaymentDtoIn';
import { queryClient } from '@/hooks/QueryClient';
import { queryKeys } from '@/hooks/QueryKeys';
import { UpdateSupportRequestStatusDtoOut } from '@/models/out/UpdateSupportRequestStatusDtoOut';
import { ManagementSupportRequestDtoIn } from '@/models/in/ManagementSupportRequestDtoIn';
import { PaymentMethodType } from '@/models/common/PaymentMethodType';
import useReturnedPaymentFeature from '@/hooks/useReturnedPaymentFeature';
import SupplierApprovalGate from '../SupplierApprovalGate/SupplierApprovalGate';
import alertIcon from '@/assets/icons/alert-circle.svg';
import styles from './StatusChangeDialog.module.css';

interface StatusChangeDialogProps {
  open: boolean;
  onClose: () => void;
  correlationId: string;
  counter: number | null;
  isInInitialStatusProcess: boolean;
  newStatus: string;
  /**
   * The request being transitioned. Used by the supplier approval gate to
   * check the supplier's tax-withholding state. Optional - the dialog still
   * works if the parent can't supply it (the gate just becomes a no-op).
   */
  request?: ManagementSupportRequestDtoIn | null;
  onSuccess: () => void;
}

const StatusChangeDialog = ({ open, onClose, correlationId, counter, isInInitialStatusProcess, newStatus, request, onSuccess }: StatusChangeDialogProps) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [showInPersonalArea, setShowInPersonalArea] = useState(true);
  const [sendEmail, setSendEmail] = useState(false);
  // HAT-1395: on by default - a payment bounces because the account is wrong. Off is for the
  // rarer bounce whose account is fine and only needs paying again.
  const [requestNewBankAccount, setRequestNewBankAccount] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isReturnedPaymentOn = useReturnedPaymentFeature();
  const canRequestNewBankAccount =
    isReturnedPaymentOn &&
    newStatus === 'PaymentReturned' &&
    request?.paymentMethodType === PaymentMethodType.BankAccount &&
    !request?.isThirdPartyPayment;
  // A month already «תשלום חזר», opened from the select's «בקשת חשבון חדש» entry: the status stays,
  // and asking for another account is the whole point, so there is no toggle to turn it off.
  const asksAgain = canRequestNewBankAccount && String(request?.status) === newStatus;
  const asksForNewBankAccount = canRequestNewBankAccount && (asksAgain || requestNewBankAccount);
  // The returned-payment mail goes out whatever the toggle says, and replaces the status mail.
  const mailsCoordinator = asksForNewBankAccount || sendEmail;
  // Who else pays to the account: their payments would bounce too, and their coordinators are
  // asked for another account as well - the office should know that before it deletes.
  const { data: impact } = useGetAuthApi<ReturnedPaymentImpactDtoIn>(
    asksForNewBankAccount && counter != null ? `management/support-requests/${correlationId}/${counter}/returned-payment-impact` : ''
  );
  const otherRequests = (impact?.otherRequestsInFund ?? 0) + (impact?.requestsInOtherFunds ?? 0);
  // The supplier approval gate runs over this dialog; while it's open we hide
  // nothing (the gate renders its own modal layer) but we delay the API call
  // until the user accepts/cancels each gate step.
  const [gateOpen, setGateOpen] = useState(false);

  const { mutate: updateStatus, isPending } = usePutAuthApi<void, object, UpdateSupportRequestStatusDtoOut>('management/support-requests/status');

  const performUpdate = useCallback(() => {
    setError(null);

    const body: UpdateSupportRequestStatusDtoOut = {
      correlationId,
      counter: isInInitialStatusProcess ? null : counter,
      isInInitialStatusProcess,
      newStatus,
      comment: comment.trim() || undefined,
      showInPersonalArea,
      sendEmail: asksForNewBankAccount ? false : sendEmail,
      requestNewBankAccount: asksForNewBankAccount,
    };

    updateStatus(
      { body },
      {
        onSuccess: () => {
          onSuccess();
          queryClient.refetchQueries({
            predicate: (query) => queryKeys.supportRequests.isManagementQuery(query.queryKey),
          });
          setComment('');
          onClose();
        },
        onError: (err) => {
          console.error('Error updating support request status:', err);
          setError(t('errorUpdatingStatus'));
        },
      }
    );
  }, [correlationId, counter, isInInitialStatusProcess, newStatus, comment, showInPersonalArea, sendEmail, asksForNewBankAccount, updateStatus, onSuccess, onClose, t]);

  const handleSubmit = () => {
    setGateOpen(true);
  };

  const handleGateContinue = useCallback(() => {
    setGateOpen(false);
    performUpdate();
  }, [performUpdate]);

  const handleGateCancel = useCallback(() => {
    setGateOpen(false);
  }, []);

  const gateRequests = request ? [request] : [];

  const handleClose = () => {
    setComment('');
    setShowInPersonalArea(true);
    setSendEmail(false);
    setRequestNewBankAccount(true);
    setError(null);
    setGateOpen(false);
    onClose();
  };

  return (
    <>
      <SupplierApprovalGate
        open={gateOpen}
        selectedRequests={gateRequests}
        newStatus={newStatus}
        onContinue={handleGateContinue}
        onCancel={handleGateCancel}
      />
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => !isOpen && handleClose()}
      title={asksAgain ? t('returnedPayment.askNewAccountTitle') : t('supportRequestsManagement.changeStatus')}
      // Sized to its content, like the bulk window: full screen, it was mostly empty.
      maxWidth="720px"
      fitContent
    >
      <div className={styles.content}>
        <p className="body-md-regular text-(--font-body-1)">
          {asksAgain
            ? t('returnedPayment.askNewAccountIntro')
            : t('supportRequestsManagement.statusChangeConfirmation', {
                status: t(newStatus),
              })}
        </p>

        {request?.frozenAmount != null && newStatus !== 'DonePendingCompletion' && newStatus !== 'DoneCompletionReturned' && (
          <div className={styles.freezeReleaseWarning}>
            <img src={alertIcon} alt="" className={styles.freezeReleaseIcon} />
            <span className="body-sm-regular">{t('freezeReleaseWarning')}</span>
          </div>
        )}

        {/* Why there is no toggle: nothing of the supported's to delete. Without it the office read
            the missing toggle as the feature not working (a medical-expenses request with no account). */}
        {isReturnedPaymentOn && newStatus === 'PaymentReturned' && !canRequestNewBankAccount && (
          <p className="body-sm-regular text-(--font-body-2)">
            {request?.isThirdPartyPayment ? t('returnedPayment.noAskThirdParty') : t('returnedPayment.noAskNoAccount')}
          </p>
        )}

        {canRequestNewBankAccount && (
          <div className={styles.emailOption}>
            {!asksAgain && (
              <ToggleSwitch
                label={t('returnedPayment.requestNewAccountToggle')}
                checked={requestNewBankAccount}
                onChange={setRequestNewBankAccount}
              />
            )}
            {/* Always there, only its text changes with the toggle: appearing and disappearing it
                pushed everything below it up and down. */}
            <div className={`${styles.newAccountNotice} ${asksForNewBankAccount ? '' : styles.newAccountNoticeOff}`}>
              <img src={alertIcon} alt="" className={styles.newAccountNoticeIcon} />
              <div className={`body-sm-regular ${styles.noticeTexts}`}>
                {/* What saving does, a step a line, in the order it happens - one run-on sentence
                    left the office unsure whose account, and what «תחכה לחשבון חדש» meant. */}
                <div className={asksForNewBankAccount ? '' : styles.noticeTextHidden} aria-hidden={!asksForNewBankAccount}>
                  <span className="body-sm-semibold">{t('returnedPayment.stepsLead')}</span>
                  <ul className={styles.noticeSteps}>
                    <li>
                      {t('returnedPayment.stepDelete', {
                        account: `${request?.bankNumber ?? ''}-${request?.bankBranchNumber ?? ''}-${request?.bankAccountNumber ?? ''}`,
                      })}
                    </li>
                    <li>{t('returnedPayment.stepMail')}</li>
                    <li>{t('returnedPayment.stepBack')}</li>
                    {otherRequests > 0 && (
                      <li>
                        <strong>
                          {impact?.requestsInOtherFunds
                            ? t('returnedPayment.impactWithOtherFunds', { count: otherRequests })
                            : t('returnedPayment.impactInFund', { count: otherRequests })}
                        </strong>
                      </li>
                    )}
                  </ul>
                </div>
                {!asksAgain && (
                  <div className={requestNewBankAccount ? styles.noticeTextHidden : ''} aria-hidden={requestNewBankAccount}>
                    {t('returnedPayment.keepAccountExplanation')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className={styles.emailOption}>
          <ToggleSwitch
            label={t('officeComments.showInPersonalArea')}
            checked={showInPersonalArea}
            onChange={setShowInPersonalArea}
          />
          {/* With a new account asked for, the coordinator is mailed whatever - the toggle shows it
              on and locked rather than vanishing, so the office sees that a mail goes out. */}
          <ToggleSwitch
            label={asksForNewBankAccount ? t('returnedPayment.mailAlwaysSent') : t('sendEmailNotification')}
            checked={asksForNewBankAccount || sendEmail}
            onChange={setSendEmail}
            disabled={asksForNewBankAccount}
          />
        </div>

        <div className={styles.noteSection}>
          <label htmlFor="note" className="body-sm-semibold text-(--font-body-1)">
            {t('note')}
          </label>
          <textarea
            id="note"
            className={styles.textarea}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('supportRequestsManagement.notePlaceholder')}
            rows={3}
          />
          {/* Keeps its line when hidden, so turning the mail on or off does not move the buttons. */}
          <p className="body-xs-sm-regular text-(--font-body-2)" style={{ visibility: mailsCoordinator ? 'visible' : 'hidden' }}>
            {t('noteSentByEmail')}
          </p>
        </div>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.actions}>
          <button className={styles.secondaryButton} onClick={handleClose} disabled={isPending}>
            {t('close')}
          </button>
          <button className={styles.primaryButton} onClick={handleSubmit} disabled={isPending || gateOpen}>
            {isPending ? t('loading') : mailsCoordinator ? t('saveAndSend') : t('save')}
          </button>
        </div>
      </div>
    </MainDialogLayout>
    </>
  );
};

export default StatusChangeDialog;
